import { lazy, useEffect } from "react";
import { Navigate, Route, Routes, useNavigate } from "react-router-dom";
import Shell from "./components/Shell";
import SettingsLayout from "./components/SettingsLayout";
import { onUnauthenticated } from "./lib/api";
import { useAuth } from "./lib/auth";

/*
 * Each screen is its own chunk, pulled the first time it is opened.
 *
 * The app used to be one ~690 kB bundle: every page, chart and form downloaded
 * and parsed before the first screen could paint. Splitting the routes means a
 * cold load fetches the shell and the one page asked for, and the rest arrive on
 * navigation (cached after). The Suspense boundary is in `Shell`, around the
 * content Outlet, so a page's chunk loads behind the same LogoLoader a data
 * fetch shows, with the nav still in place.
 *
 * Kept eager on purpose: `Shell` (the frame around everything), `Login` and
 * `ChangePassword` (the pre-auth paths -- splitting them would only add a blank
 * flash before the first screen a signed-out person sees), and the tiny
 * `Scaffold`/`NotFound` placeholders (`SCAFFOLDS` is a value read at render).
 */
import Login from "./pages/Login";
import ChangePassword from "./pages/ChangePassword";
import Scaffold, { SCAFFOLDS } from "./pages/Scaffold";
import NotFound from "./pages/NotFound";

const TransactionsPage = lazy(() => import("./pages/Transactions"));
const DashboardPage = lazy(() => import("./pages/Dashboard"));
const AccountsPage = lazy(() => import("./pages/Accounts"));
const OperationsPage = lazy(() => import("./pages/Operations"));
const AddAccountPage = lazy(() => import("./pages/AddAccount"));
const AccountDetailPage = lazy(() => import("./pages/AccountDetail"));
const EntityDetailPage = lazy(() => import("./pages/EntityDetail"));
const ConnectionsPage = lazy(() => import("./pages/Connections"));
// People and Activity are now one Settings entry with an in-page switch
// (pages/PeopleActivity); /activity redirects into it.
const PeopleActivityPage = lazy(() => import("./pages/PeopleActivity"));
const FeedbackPage = lazy(() => import("./pages/Feedback"));
// Alert emails and Snapshot emails are now sections of the System page
// (pages/SettingsSystem imports them with embedded); /alerts and
// /snapshot-emails redirect there.
const FloorsPage = lazy(() => import("./pages/Floors"));
const ReadingsPage = lazy(() => import("./pages/Readings"));
const ObligationsPage = lazy(() => import("./pages/Obligations"));
const SettingsSystemPage = lazy(() => import("./pages/SettingsSystem"));
// Check printing is a section of the System page now (SettingsSystem imports it
// with embedded); /settings/checks redirects there.
const WhatsNewLogPage = lazy(() => import("./pages/WhatsNewLog"));
const BooksAccountPage = lazy(() => import("./pages/BooksAccount"));
const BooksAskPage = lazy(() => import("./pages/BooksAsk"));
const BooksChartPage = lazy(() => import("./pages/BooksChart"));
const BooksProfitLossPage = lazy(() => import("./pages/BooksProfitLoss"));
const BooksTransactionsPage = lazy(() => import("./pages/BooksTransactions"));
const BooksTrialBalancePage = lazy(() => import("./pages/BooksTrialBalance"));
const BooksBalanceSheetPage = lazy(() => import("./pages/BooksBalanceSheet"));
const BooksSyncPage = lazy(() => import("./pages/BooksSync"));
const RulesPage = lazy(() => import("./pages/Rules"));
const PayrollPage = lazy(() => import("./pages/Payroll"));
const VendorsPage = lazy(() => import("./pages/Vendors"));
const VendorDetailPage = lazy(() => import("./pages/VendorDetail"));
const InvoicesPage = lazy(() => import("./pages/Invoices"));
const ApInboxPage = lazy(() => import("./pages/ApInbox"));
const ApprovalsPage = lazy(() => import("./pages/Approvals"));
const PaymentsPage = lazy(() => import("./pages/Payments"));

/**
 * Routing, and the two redirects that keep the client in step with the server.
 *
 * Neither of them is a security boundary. `auth.active_user` refuses the
 * request; these only decide which screen a person looks at while that
 * happens, so a determined browser gets a 403 instead of a nicer page.
 */
export default function App() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  // The API client answers 401 and 403-needs-password once for the whole app.
  // This is where it learns how to move the browser.
  useEffect(() => {
    onUnauthenticated((to) => navigate(to, { replace: true }));
  }, [navigate]);

  if (loading) {
    // Deliberately blank rather than a login form. Flashing /login at somebody
    // who is already signed in, every cold load, trains people to ignore it.
    return <div className="auth-screen" aria-busy="true" />;
  }

  if (!user) {
    return (
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  if (user.must_change_password) {
    return (
      <Routes>
        <Route path="/password" element={<ChangePassword />} />
        <Route path="*" element={<Navigate to="/password" replace />} />
      </Routes>
    );
  }

  return (
    <Routes>
      <Route path="/login" element={<Navigate to="/" replace />} />
      <Route path="/password" element={<ChangePassword />} />

      <Route element={<Shell />}>
        <Route index element={<DashboardPage />} />
        {/* Position retired into the Dashboard (redesign/dashboard, 2026-10-22):
            the available-cash statement IS the Dashboard now, and "where it sits
            today" is the Accounts explorer. /position and the old /snapshots both
            redirect to the Dashboard so deep links and bookmarks still land. */}
        <Route path="/position" element={<Navigate to="/" replace />} />
        <Route path="/snapshots" element={<Navigate to="/" replace />} />
        <Route path="/accounts" element={<AccountsPage />} />
        {/* Static segment, declared before the dynamic :id so "new" is never
            read as an account id. React Router ranks static over dynamic too,
            but order makes the intent plain. Builder gate is inside the page. */}
        <Route path="/accounts/new" element={<AddAccountPage />} />
        <Route path="/accounts/:id" element={<AccountDetailPage />} />
        {/* ⚠️ Entities merged INTO the Accounts explorer (P90): the list page is
            gone and its route redirects, so old deep links and bookmarks still
            land. The entity drill-down below stays — it is reached from the
            explorer's Entity-lens group headers. */}
        <Route path="/entities" element={<Navigate to="/accounts" replace />} />
        <Route path="/entities/:id" element={<EntityDetailPage />} />
        {/* ⚠️ Institutions + Held folded INTO Connections (P90) as cuts of one
            bank-connection hub. The routes redirect, carrying the cut in the
            query so old deep links (the dashboard's /connections/held among
            them) still land on the right one. */}
        <Route
          path="/institutions"
          element={<Navigate to="/connections" replace />}
        />
        <Route
          path="/connections/held"
          element={<Navigate to="/connections?v=held" replace />}
        />
        {/* ⚠️ Settings is ONE area now (Jonathan, 2026-10-22): a flat in-page
            side rail beside the main nav (SettingsLayout), not three sidebar
            fly-out groups. Every page keeps its own top-level path, so every deep
            link still lands — the layout route only adds the rail around them. */}
        <Route element={<SettingsLayout />}>
          <Route path="/floors" element={<FloorsPage />} />
          {/* Alert-recipients config ("who is emailed on a breach") is back in
              Settings (redesign/dashboard): the LIVE alerts live on Operations,
              this is the subscription coverage view. */}
          <Route path="/alerts" element={<Navigate to="/settings/system" replace />} />
          <Route path="/snapshot-emails" element={<Navigate to="/settings/system" replace />} />
          <Route path="/connections" element={<ConnectionsPage />} />
          {/* People + Activity merged into one entry with an in-page switch
              (Jonathan, 2026-11-03). /activity redirects into the Activity cut. */}
          <Route path="/people" element={<PeopleActivityPage />} />
          <Route path="/activity" element={<Navigate to="/people?v=activity" replace />} />
          <Route path="/feedback" element={<FeedbackPage />} />
          <Route path="/settings/system" element={<SettingsSystemPage />} />
          <Route path="/settings/checks" element={<Navigate to="/settings/system" replace />} />
          {/* The ledger of every "what's new" note (Jonathan, 2026-11-03): the
              reload popover is gone once you reload, this keeps the history. */}
          <Route path="/settings/whats-new" element={<WhatsNewLogPage />} />
        </Route>
        {/* Operations (redesign/dashboard, 2026-10-22): the action surface —
            exceptions, the bookkeeping bands, the system-health footer — off the
            Dashboard. Renamed from the slice-1 "Needs You"; /needs-you redirects
            so that short-lived link still lands. */}
        <Route path="/operations" element={<OperationsPage />} />
        <Route path="/needs-you" element={<Navigate to="/operations" replace />} />
        {/* Commitments + Balances moved under the Accounts fly-out, so they render
            as plain pages (no Settings rail). /readings keeps its path as the
            Balances page so existing deep links still land. */}
        <Route path="/commitments" element={<ObligationsPage />} />
        <Route path="/readings" element={<ReadingsPage />} />
        {/* Counterparties folded into the Bank transactions page (P90). */}
        <Route
          path="/counterparties"
          element={<Navigate to="/transactions?v=counterparties" replace />}
        />
        <Route path="/transactions" element={<TransactionsPage />} />

        {/* The book side. `/books` is the chart, and it is declared before
            the two below it only for readability -- react-router matches on
            the full path, not on declaration order. */}
        <Route path="/books" element={<BooksChartPage />} />
        {/* C1: an account in the chart, and every line posted to it.
            The FILE is in the path because a QuickBooks account id is
            unique only inside one company file. */}
        <Route
          path="/books/accounts/:fileId/:qbdId"
          element={<BooksAccountPage />}
        />
        <Route path="/books/transactions" element={<BooksTransactionsPage />} />
        {/* The classification rules workbench (Chaim §4/§6.2): sign off the
            accounting rules and approve learned vendor candidates. Read-only to
            QuickBooks — it edits Orbit's own rule tables. */}
        <Route path="/books/rules" element={<RulesPage />} />
        <Route path="/payroll" element={<PayrollPage />} />
        <Route path="/books/trial-balance" element={<BooksTrialBalancePage />} />
        <Route path="/books/balance-sheet" element={<BooksBalanceSheetPage />} />
        <Route path="/books/profit-loss" element={<BooksProfitLossPage />} />
        <Route path="/books/ask" element={<BooksAskPage />} />
        <Route path="/books/sync" element={<BooksSyncPage />} />

        {/* Payables. Built ahead of the AP inbox and the payment rail on
            purpose: the controls these screens carry -- four eyes on a
            vendor's bank details, a decision that cannot be made without a
            reason, a release only a person can record -- are cheap to build
            into empty tables and expensive to retrofit onto real ones. */}
        <Route path="/vendors" element={<VendorsPage />} />
        <Route path="/vendors/:id" element={<VendorDetailPage />} />
        <Route path="/invoices" element={<InvoicesPage />} />
        <Route path="/ap-inbox" element={<ApInboxPage />} />
        <Route path="/approvals" element={<ApprovalsPage />} />
        <Route path="/payments" element={<PaymentsPage />} />

        {SCAFFOLDS.map((scaffold) => (
          <Route
            key={scaffold.path}
            path={scaffold.path}
            element={<Scaffold {...scaffold} />}
          />
        ))}

        <Route path="*" element={<NotFound />} />
      </Route>
    </Routes>
  );
}
